import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  CircularProgress
} from '@mui/material';
import {
  LocalHospital,
  Person,
  Search,
  CheckCircle,
  Schedule,
  Cancel,
  Visibility
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../components/MainLayout';
import { useFeedback } from '../components/EnhancedFeedbackProvider';
import { useDataContext } from '../contexts/DataContext';

const Treatments = () => {
  const navigate = useNavigate();
  const { showError } = useFeedback();
  const { treatments: allTreatments, patients, loading } = useDataContext();
  const [filteredTreatments, setFilteredTreatments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    filterTreatments();
  }, [allTreatments, searchTerm, statusFilter]);

  const filterTreatments = () => {
    let filtered = allTreatments;

    // Filtre par recherche
    if (searchTerm) {
      filtered = filtered.filter(treatment => {
        const patient = patients.find(p => p.id === treatment.patientId);
        return patient?.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
               patient?.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
               treatment.medication_name?.toLowerCase().includes(searchTerm.toLowerCase());
      });
    }

    // Filtre par statut
    if (statusFilter) {
      filtered = filtered.filter(treatment => {
        const status = getTreatmentStatus(treatment.start_date, treatment.end_date);
        return status.label.toLowerCase().replace(' ', '') === statusFilter.toLowerCase().replace(' ', '');
      });
    }

    setFilteredTreatments(filtered);
  };

  const getTreatmentStatus = (startDate, endDate) => {
    const now = new Date();
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : null;

    if (end && now > end) {
      return { color: 'success', label: 'Terminé', icon: <CheckCircle /> };
    } else if (now >= start) {
      return { color: 'primary', label: 'En cours', icon: <Schedule /> };
    } else {
      return { color: 'warning', label: 'À venir', icon: <Schedule /> };
    }
  };

  const handleRowClick = (treatment) => {
    if (treatment.patientId) {
      navigate(`/patients/${treatment.patientId}`);
    }
  };

  const handleViewPatient = (e, treatment) => {
    e.stopPropagation(); // Empêcher le clic sur la ligne
    if (treatment.patientId) {
      navigate(`/patients/${treatment.patientId}`);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  return (
    <MainLayout>
      <Box>
        {/* En-tête */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1" fontWeight="bold">
            Traitements
          </Typography>
        </Box>

        {/* Filtres */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
              <TextField
                placeholder="Rechercher par patient ou médicament..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 300 }}
              />
              
              <FormControl sx={{ minWidth: 150 }}>
                <InputLabel>Statut</InputLabel>
                <Select
                  value={statusFilter}
                  label="Statut"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="">Tous</MenuItem>
                  <MenuItem value="encours">En cours</MenuItem>
                  <MenuItem value="terminé">Terminé</MenuItem>
                  <MenuItem value="àvenir">À venir</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </CardContent>
        </Card>

        {/* Indicateur de chargement */}
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress size={40} />
            <Typography variant="body2" color="text.secondary" ml={2}>
              Chargement des traitements...
            </Typography>
          </Box>
        )}

        {/* Tableau des traitements */}
        {!loading && (
          <Card>
            <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Patient</TableCell>
                  <TableCell>Médicament</TableCell>
                  <TableCell>Dosage</TableCell>
                  <TableCell>Fréquence</TableCell>
                  <TableCell>Début</TableCell>
                  <TableCell>Fin</TableCell>
                  <TableCell>Statut</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTreatments
                  .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((treatment) => {
                    const status = getTreatmentStatus(treatment.start_date, treatment.end_date);
                    
                    return (
                      <TableRow
                        key={treatment.id}
                        hover
                        onClick={() => handleRowClick(treatment)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Person />
                            <Typography variant="body2">
                              {(() => {
                                const patient = patients.find(p => p.id === treatment.patientId);
                                return patient ? `${patient.firstName} ${patient.lastName}` : 'Patient inconnu';
                              })()}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <LocalHospital />
                            <Typography variant="body2">
                              {treatment.medication_name || 'N/A'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{treatment.dosage || 'N/A'}</TableCell>
                        <TableCell>{treatment.frequency || 'N/A'}</TableCell>
                        <TableCell>{formatDate(treatment.start_date)}</TableCell>
                        <TableCell>{formatDate(treatment.end_date)}</TableCell>
                        <TableCell>
                          <Chip
                            icon={status.icon}
                            label={status.label}
                            color={status.color}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton
                            onClick={(e) => handleViewPatient(e, treatment)}
                            color="primary"
                            size="small"
                            title="Voir les détails du patient"
                          >
                            <Visibility />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredTreatments.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Lignes par page:"
            labelDisplayedRows={({ from, to, count }) => 
              `${from}-${to} sur ${count !== -1 ? count : `plus de ${to}`}`
            }
          />
          </Card>
        )}
      </Box>
    </MainLayout>
  );
};

export default Treatments;